import VueWrapper from '@/components/core/Vue/vue.wrapper';
import {TerminalBlockDataModel} from '@/sdk';
import {CableCoresApi, TerminalBlockApi} from '@/sdk/api-services';
import {Component, Prop} from 'vue-property-decorator';

interface UtilizationRow {
    id: string | number;
    crossSection: number | string;
    ratedCurrent: number | string;
    quantity: number;
    quantityMax: number;
    utilizationPercent: number; // 0-100
}

@Component
export default class JunctionBoxUtilizationTableComponent extends VueWrapper {
    @Prop({required: true}) readonly locationId!: string | number;

    @Prop({
        type: String,
        default: null,
    })
    readonly terminalBlockId!: string | null;

    loading = false;
    rows: UtilizationRow[] = [];
    private terminalBlockApi = new TerminalBlockApi();
    private cableCoresApi = new CableCoresApi();

    created() {
        this.fetchData();
    }

    async fetchData() {
        try {
            if (!this.terminalBlockId) {
                this.rows = [];
                return;
            }
            this.loading = true;
            this.LoaderSrv.showFullScreenLoader();
            // 1) Load structured data (currents, cross-sections, matrix) and connected cores of this location in parallel
            const [structured, connectedCores] = await Promise.all([
                this.terminalBlockApi.getById(this.terminalBlockId),
                this.cableCoresApi.getConnectedCoresOfLocation(String(this.locationId)),
            ]);

            const currents: TerminalBlockDataModel[] = (structured && structured.currents) || [];
            const crossSections: TerminalBlockDataModel[] = (structured && structured.crossSections) || [];
            const matrix = (structured && structured.matrix) || ({} as Record<string, Record<string, string>>);

            // 2) Prepare value->id lookup for matching pins to matrix cells
            const currentIdByValue = buildValueToIdMap(currents);
            const crossSectionIdByValue = buildValueToIdMap(crossSections);

            // 3) Count used quantities per (currentId, crossSectionId) by matching connected cores' own current/cross-section values
            const usedCounts: Record<string, number> = {};
            for (const core of connectedCores || []) {
                const coreCurrentVal = core.coreRatedCurrent;
                const coreCSVal = core.coreCrossSection;
                if (coreCurrentVal == null || coreCSVal == null) continue;

                const curId = findIdByComparableValue(currentIdByValue, coreCurrentVal);
                const csId = findIdByComparableValue(crossSectionIdByValue, coreCSVal);
                if (!curId || !csId) continue;

                const key = `${curId}:${csId}`;
                usedCounts[key] = (usedCounts[key] || 0) + 1;
            }

            // 4) Build rows: quantity from usedCounts; quantityMax from matrix cell value
            const rows: UtilizationRow[] = [];
            for (const cur of currents) {
                const curId = cur.id || '';
                const currentLabel = numOrStr(cur.value);
                const row = matrix[curId] || {};
                for (const cs of crossSections) {
                    const csId = cs.id || '';
                    const capStr = row[csId];
                    const cap = isFiniteNumberStr(capStr) ? Math.max(0, Math.floor(parseFloat(capStr as string))) : 0;
                    const qty = usedCounts[`${curId}:${csId}`] || 0;
                    if (cap <= 0 && qty <= 0) continue; // skip empty

                    const csLabel = numOrStr(cs.value);
                    const percent = cap > 0 ? (qty / cap) * 100 : 0;
                    rows.push({
                        id: `${curId}:${csId}`,
                        crossSection: csLabel,
                        ratedCurrent: currentLabel,
                        quantity: qty,
                        quantityMax: cap,
                        utilizationPercent: percent,
                    });
                }
            }

            this.rows = rows;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to load junction box utilization', e);
            this.rows = [];
        } finally {
            this.loading = false;
            this.LoaderSrv.hideFullScreenLoader();
        }
    }

    get totalUtilizationPercent() {
        if (!this.rows.length) return 0;
        // Simple sum of all utilizationPercent values
        return this.rows.reduce((sum, row) => sum + row.utilizationPercent, 0);
    }

    utilClass(percent: number) {
        if (percent >= 85) return 'high';
        if (percent >= 70) return 'medium';
        return 'low';
    }

    formatPercent(p: number) {
        return `${p.toFixed(0)}%`;
    }
}

// Helpers
function isFiniteNumberStr(v: unknown): boolean {
    if (typeof v !== 'string') return false;
    const n = parseFloat(v);
    return Number.isFinite(n);
}

function numOrStr(v?: string | null): number | string {
    if (!v) return '';
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : v;
}

// Helpers for mapping values to ids and extracting pins
function normalizeComparable(val: any): {num?: number; str?: string} {
    if (val == null) return {};
    const s = String(val).trim();
    const n = parseFloat(s);
    return Number.isFinite(n) ? {num: n} : {str: s.toLowerCase()};
}

function buildValueToIdMap(list: TerminalBlockDataModel[]): Record<string, string> {
    const map: Record<string, string> = {};
    for (const item of list) {
        const key = JSON.stringify(normalizeComparable(item.value));
        if (!map[key]) map[key] = item.id || '';
    }
    return map;
}

function findIdByComparableValue(map: Record<string, string>, value: any): string | null {
    const key = JSON.stringify(normalizeComparable(value));
    return map[key] || null;
}

function getFirstProp(obj: any, keys: string[]): any {
    for (const k of keys) {
        if (obj && obj[k] != null) return obj[k];
    }
    return null;
}

// extractPinAtLocation removed: cores contain current/cross-section; location filter is handled by API
